import isEqual from 'react-fast-compare';

import {makeAnalyticsEventId} from 'analytics/Analytics';
import {Owner} from 'auth/PermissionConstants';
import {
  AutomationInputFileConfigSpec,
  AutomationOutputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
  DestinationInfoAction,
  OutputFileAction,
  STORAGE_COLUMN_TYPES,
  isTransfer,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {BenchlingAction} from 'redux-stores/Action';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {stringify} from 'schemas/spec/SchemaSpecUtil';
import {ErrorState} from 'ui-library/spec-editor/getSpecError';
import {Optional} from 'util/Types';

import {AssaySchemaSpec, RunSchemaSpec, SchemaSpec} from './SchemaConfigConstants';
import {SchemaConfigActionType} from './SchemaConfigReduxActions';

export interface SchemaConfigUIState {
  schemaId: string | null;
  sessionId: string;
  specString: string | null;
  inputFileConfigs: Array<AutomationInputFileConfigSpec> | null;
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec> | null;
  errorState: ErrorState | null;
  ownerChoices: Array<Owner> | null;
  ownerId: string | null;
  name: string | null;
  fields: Array<SchemaField | ClientSchemaField> | null;
  sqlIdentifier: string | null;
  hasUnsavedData: boolean;
  hasUnsavedInputFileConfigData: boolean;
  hasUnsavedOutputFileConfigData: boolean;
  allowCreateRunFromNotebook: boolean;
  allowInsertRunFromInbox: boolean;
  shouldRecordEquipment: boolean;
  connectionSchemaId: string | null;
  analysisTemplateId: string | null;
  analysisTemplateVariableMapping: Record<string, string> | null;
}

export const SCHEMA_CONFIG_UI_INITIAL_STATE = {
  schemaId: null,
  sessionId: makeAnalyticsEventId(),
  specString: null,
  inputFileConfigs: null,
  outputFileConfigs: null,
  errorState: null,
  ownerChoices: null,
  ownerId: null,
  name: null,
  fields: null,
  sqlIdentifier: null,
  hasUnsavedData: false,
  hasUnsavedInputFileConfigData: false,
  hasUnsavedOutputFileConfigData: false,
  allowCreateRunFromNotebook: true,
  allowInsertRunFromInbox: true,
  shouldRecordEquipment: false,
  connectionSchemaId: null,
  analysisTemplateId: null,
  analysisTemplateVariableMapping: null,
};

export function isAssaySchemaSpec(spec: SchemaSpec): spec is AssaySchemaSpec {
  return spec.hasOwnProperty('fields');
}

function isLabAutoRunSchemaSpec(spec: SchemaSpec): spec is RunSchemaSpec {
  const hasInputFileConfigs =
    spec.hasOwnProperty('automationInputFileConfigs') &&
    (spec as RunSchemaSpec).automationInputFileConfigs != null;
  const hasOutputFileConfigs =
    spec.hasOwnProperty('automationOutputFileConfigs') &&
    (spec as RunSchemaSpec).automationOutputFileConfigs != null;

  return hasInputFileConfigs || hasOutputFileConfigs;
}

function hasStorageColumns(outputFileConfig: AutomationOutputFileConfigSpec): boolean {
  const columnTypes = outputFileConfig.columnTypes
    ? outputFileConfig.columnTypes
    : Object.values(outputFileConfig.columnTypesByName).map((val) => val.columnType);
  for (const storageColumnType of STORAGE_COLUMN_TYPES) {
    if (columnTypes.includes(storageColumnType)) {
      return true;
    }
  }
  return false;
}

function getActionFromOutputFileConfig(outputFileConfig: AutomationOutputFileConfigSpec): OutputFileAction {
  const doesTransfers = hasStorageColumns(outputFileConfig) || outputFileConfig.destinationInfo != null;
  if (outputFileConfig.entitySchemaInfo) {
    if (doesTransfers) {
      if (!outputFileConfig.shouldPerformPlateTransfer) {
        return outputFileConfig.resultSchemaInfo
          ? OutputFileAction.TRANSFORMATION_TRANSFER_AND_RESULTS
          : OutputFileAction.TRANSFORMATION_TRANSFER;
      } else {
        return outputFileConfig.resultSchemaInfo
          ? OutputFileAction.REGISTRATION_AND_RESULTS
          : OutputFileAction.PURE_REGISTRATION;
      }
    }
    return outputFileConfig.resultSchemaInfo
      ? OutputFileAction.REGISTRATION_AND_RESULTS
      : OutputFileAction.PURE_REGISTRATION;
  }
  if (doesTransfers) {
    if (outputFileConfig.resultSchemaInfo) {
      return OutputFileAction.TRANSFER_AND_RESULTS;
    } else if (!outputFileConfig.shouldPerformPlateTransfer) {
      return OutputFileAction.PURE_TRANSFER;
    } else {
      return OutputFileAction.NO_ACTION;
    }
  }
  if (outputFileConfig.analysisTemplate && !outputFileConfig.shouldCreateDataset) {
    return OutputFileAction.RUN_ANALYSIS_TEMPLATE;
  }
  return outputFileConfig.resultSchemaInfo ? OutputFileAction.PURE_RESULTS : OutputFileAction.NO_ACTION;
}

export function getClientOutputFileConfigs(
  outputFileConfigs: Optional<Array<AutomationOutputFileConfigSpec>>
): Array<ClientAutomationOutputFileConfigSpec> | null {
  if (!outputFileConfigs) {
    return null;
  }
  return outputFileConfigs.map((outputFileConfig) => {
    const actionType = getActionFromOutputFileConfig(outputFileConfig);
    const clientConfig: ClientAutomationOutputFileConfigSpec = {
      ...outputFileConfig,
      actionType,
    };
    if (isTransfer(actionType, outputFileConfig.shouldPerformPlateTransfer)) {
      clientConfig.destinationActionType =
        outputFileConfig.destinationInfo != null
          ? DestinationInfoAction.CREATE_OR_UPDATE
          : DestinationInfoAction.USE_EXISTING;
    }
    return clientConfig;
  });
}

export function getServerOutputFileConfigs(
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec>
): Array<AutomationOutputFileConfigSpec> {
  return outputFileConfigs.map((config: ClientAutomationOutputFileConfigSpec) => {
    const serverConfig: AutomationOutputFileConfigSpec & {
      actionType?: OutputFileAction;
      destinationActionType?: DestinationInfoAction;
    } = {...config};
    delete serverConfig.destinationActionType;
    return serverConfig;
  });
}

function parseSpec(specString: string | null): RunSchemaSpec | null {
  try {
    return JSON.parse(specString!);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    return null;
  }
}

export default function reduceSchemaConfig(
  state: SchemaConfigUIState = SCHEMA_CONFIG_UI_INITIAL_STATE,
  action: BenchlingAction
): SchemaConfigUIState {
  switch (action.type) {
    case SchemaConfigActionType.SCHEMA_SPEC_FETCHED: {
      const {schemaId, spec} = action;
      const inputFileConfigs = isLabAutoRunSchemaSpec(spec) ? spec.automationInputFileConfigs : null;
      const outputFileConfigs = isLabAutoRunSchemaSpec(spec)
        ? getClientOutputFileConfigs(spec.automationOutputFileConfigs)
        : null;
      const allowCreateRunFromNotebook =
        // eslint-disable-next-line benchlint/no-in-operator
        'allowCreateRunFromNotebook' in spec ? spec.allowCreateRunFromNotebook : true;
      // eslint-disable-next-line benchlint/no-in-operator
      const allowInsertRunFromInbox = 'allowInsertRunFromInbox' in spec ? spec.allowInsertRunFromInbox : true;
      // eslint-disable-next-line benchlint/no-in-operator
      const shouldRecordEquipment = 'shouldRecordEquipment' in spec ? spec.shouldRecordEquipment : false;
      let sessionId = state.sessionId;
      if (schemaId == null || schemaId !== state.schemaId) {
        sessionId = makeAnalyticsEventId();
      }
      return {
        ...state,
        schemaId,
        sessionId,
        specString: stringify(spec),
        fields: isAssaySchemaSpec(spec) ? spec.fields : null,
        inputFileConfigs: inputFileConfigs || null,
        outputFileConfigs,
        errorState: null,
        hasUnsavedData: false,
        hasUnsavedInputFileConfigData: false,
        hasUnsavedOutputFileConfigData: false,
        allowCreateRunFromNotebook,
        allowInsertRunFromInbox,
        shouldRecordEquipment,
        // eslint-disable-next-line benchlint/no-in-operator
        connectionSchemaId: 'connectionSchemaId' in spec ? (spec.connectionSchemaId ?? null) : null,
        // eslint-disable-next-line benchlint/no-in-operator
        analysisTemplateId: 'analysisTemplateId' in spec ? (spec.analysisTemplateId ?? null) : null,
        // eslint-disable-next-line benchlint/no-in-operator
        analysisTemplateVariableMapping:
          // eslint-disable-next-line benchlint/no-in-operator
          'analysisTemplateVariableMapping' in spec
            ? ((spec.analysisTemplateVariableMapping as Record<string, string> | null) ?? null)
            : null,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_CHANGED: {
      const {specString} = action;
      const newSpec = parseSpec(specString);
      const hasUnsavedInputFileConfigData =
        newSpec != null && !isEqual(state.inputFileConfigs, newSpec.automationInputFileConfigs);
      const outputFileConfigs = state.outputFileConfigs
        ? getServerOutputFileConfigs(state.outputFileConfigs)
        : null;
      const hasUnsavedOutputFileConfigData =
        newSpec != null && !isEqual(outputFileConfigs, newSpec.automationOutputFileConfigs);
      return {
        ...state,
        specString,
        errorState: null,
        hasUnsavedData: true,
        hasUnsavedInputFileConfigData,
        hasUnsavedOutputFileConfigData,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_RESET_TO_DRAFT: {
      const {specString} = action;
      return {
        ...state,
        specString,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_UPDATE_FAILED: {
      const {errorState} = action;
      return {
        ...state,
        errorState,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_ALLOW_CREATE_RUN_FROM_NLAD_CHANGED: {
      const {allowCreateRunFromNotebook} = action;
      return {
        ...state,
        allowCreateRunFromNotebook,
        hasUnsavedData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_ALLOW_INSERT_RUN_FROM_INBOX_CHANGED: {
      const {allowInsertRunFromInbox} = action;
      return {
        ...state,
        allowInsertRunFromInbox,
        hasUnsavedData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_SHOULD_RECORD_EQUIPMENT_CHANGED: {
      const {shouldRecordEquipment} = action;
      return {
        ...state,
        shouldRecordEquipment,
        hasUnsavedData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_INPUT_FILE_CONFIG_CHANGED: {
      const {inputFileConfigs} = action;
      return {
        ...state,
        inputFileConfigs,
        hasUnsavedData: true,
        hasUnsavedInputFileConfigData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_OUTPUT_FILE_CONFIG_CHANGED: {
      const {outputFileConfigs} = action;
      return {
        ...state,
        outputFileConfigs,
        hasUnsavedOutputFileConfigData: true,
        hasUnsavedData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_FIELD_CHANGED: {
      const {fields} = action;
      return {
        ...state,
        fields,
        hasUnsavedData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_OWNER_CHANGED: {
      const {ownerId} = action;
      return {
        ...state,
        ownerId,
        hasUnsavedData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_NAME_CHANGED: {
      const {name} = action;
      return {
        ...state,
        name,
        hasUnsavedData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_SQL_IDENTIFIER_CHANGED: {
      const {sqlIdentifier} = action;
      return {
        ...state,
        sqlIdentifier,
        hasUnsavedData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_CONNECTION_SCHEMA_ID_CHANGED: {
      const {connectionSchemaId} = action;
      return {
        ...state,
        connectionSchemaId,
        hasUnsavedData: true,
      };
    }
    case SchemaConfigActionType.SCHEMA_SPEC_UI_RESET: {
      const {ownerChoices, ownerId} = action;
      return {
        ...SCHEMA_CONFIG_UI_INITIAL_STATE,
        ownerChoices,
        ownerId,
      };
    }
    default:
      return state;
  }
}
